import React, { useState, useCallback, useEffect } from 'react';
import { Box, Tabs, Tab, Button } from '@mui/material';
import AddTreatmentForm from './AddTreatmentForm';
import TreatmentHistory from './TreatmentHistory';
import { ncdAPI } from '../../../../services/ncd.api';
import './DiabetesFollowup.css';

const DiabetesFollowup = ({ individualId, dbScreenId, setShowDiabetesFollowup, onSubmit }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [treatmentHistory, setTreatmentHistory] = useState([]);
  const [loadingTreatment, setLoadingTreatment] = useState(true);
  const [status, setStatus] = useState("");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleClose = useCallback(() => {
    setShowDiabetesFollowup(false);
  }, [setShowDiabetesFollowup]);

  const parseDate = (dateStr) => {
    if (!dateStr) return 0;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      // DD-MM-YYYY -> YYYY-MM-DD
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
    }
    return new Date(dateStr).getTime() || 0;
  };

  const formatDiagnosis = (diag) => {
    if (!diag) return "-";

    if (diag.includes("STAGE_1")) return "Diabetes Stage 1";
    if (diag.includes("STAGE_2")) return "Diabetes Stage 2";
    if (diag.includes("NORMAL")) return "Normal";

    return diag.replaceAll("_", " ");
  };

  // Transform API → UI
  const formatData = useCallback((apiData = []) => {
    const dataArray = Array.isArray(apiData) ? apiData : [];

    return dataArray
      .sort((a, b) => {
        const aReview = a.treat?.reviewIn || a;
        const bReview = b.treat?.reviewIn || b;
        const aTime = aReview.treatDateWithTimeStamp || a.treatDateWithTimeStamp || parseDate(aReview.treatDate);
        const bTime = bReview.treatDateWithTimeStamp || b.treatDateWithTimeStamp || parseDate(bReview.treatDate);
        return bTime - aTime;
      })
      .map((item) => {
        const reviewIn = item.treat?.reviewIn || item;
        
        // Parse followUpDuration "28;0;0" format (days;weeks;months)
        const durationParts = reviewIn.docfolUp?.followUpDuration?.split(';') || ['0', '0', '0'];
        const days = durationParts[0] || '0';
        const weeks = durationParts[1] || '0';
        const months = durationParts[2] || '0';

        return {
          date: reviewIn.treatDate,
          facility: reviewIn.facility,
          fastingBS: reviewIn.fbs || reviewIn.fastngBldGlc || "-",
          postPrandialBS: reviewIn.ppbg || reviewIn.postPradBldGlc || "-",
          randomBS: reviewIn.rbs || reviewIn.rndBldGlc || "-",
          diagnosis: formatDiagnosis(reviewIn.diabetesDiagnosisModel?.selectedDiagnosis || reviewIn.dgns?.diagRresult),
          medicines: reviewIn.selMeds?.map((m) => ({
            name: (m.name || '').trim(),
            frequency: m.freq || '-',
            duration: m.dur || '-',
            quantity: m.quan || '-'
          })) || [],
          followUp: {
            duration: `${days} days`,
            reason: reviewIn.docfolUp?.followUpReason || 'Review',
            dueDate: reviewIn.docfolUp?.followUpDueDate || '-',
            days: days,
            weeks: weeks,
            months: months
          }
        };
      });
  }, []);

  const fetchTreatmentHistory = useCallback(async () => {
    if (!individualId || !dbScreenId) return;

    try {
      setLoadingTreatment(true);

      const res = await ncdAPI.getTreatmentHistoryDB(
        600,
        individualId,
        dbScreenId
      );

      const apiData = res?.data?.treatmentHistory || res?.data || [];
      setTreatmentHistory(formatData(apiData));

      const respaction = await ncdAPI.actionDetails(
        600,
        individualId,
        dbScreenId
      );

      if (respaction?.data?.curActName) {
        setStatus(respaction?.data?.curActName);
      }
    } catch (error) {
      console.error("Error fetching treatment history:", error);
    } finally {
      setLoadingTreatment(false);
    }
  }, [individualId, dbScreenId, formatData]);

  useEffect(() => {
    fetchTreatmentHistory();
  }, [fetchTreatmentHistory]);

  const handleFormSubmit = useCallback((values) => {
    // Re-fetch treatment history to get the newly added treatment
    fetchTreatmentHistory();
    onSubmit?.(values);
  }, [fetchTreatmentHistory, onSubmit]);

  return (
    <Box className="diabetes-followup-container">
      <h3 className='followup-container-title'>Diabetes Followup</h3>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className="followup-tabs"
      >
        <Tab label="Add Treatment/Follow-up" />
        <Tab label="Treatment History" />
      </Tabs>

      <Box className="tab-content-wrapper">
        {activeTab === 0 && (
          <AddTreatmentForm 
            individualId={individualId} 
            dbScreenId={dbScreenId} 
            onSubmit={handleFormSubmit} 
            treatmentHistory={treatmentHistory}
            loadingTreatment={loadingTreatment}
            onCancel={handleClose}
          />
        )}
        {activeTab === 1 && (
          <TreatmentHistory 
            treatmentData={treatmentHistory}
            loading={loadingTreatment}
            status={status}
          />
        )}
      </Box>

      <Button
        onClick={handleClose}
        sx={{
          border: "2px solid #A9A9A9",
          width: "30px",
          height: "30px",
          borderRadius: "100%",
          cursor: "pointer",
          position: "absolute",
          right: "-8px",
          top: "-10px",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "auto",
          p: 0,
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
        }}
      >
        <img src={"/images/closing.svg"} alt="Close" style={{ width: "16px" }} />
      </Button>
    </Box>
  );
};

export default DiabetesFollowup;
